import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Star, Eye, EyeOff, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminListReviews, adminUpdateReview, adminDeleteReview } from '../../../lib/api';

function Stars({ n }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${n} stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={12} className={i <= n ? 'fill-[color:var(--gold)] text-[color:var(--gold)]' : 'text-zinc-700'} />
      ))}
    </span>
  );
}

function fmt(iso) { try { return new Date(iso).toLocaleString(); } catch { return iso; } }

export default function ReviewsSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const reload = async () => {
    setLoading(true);
    try { setRows(await adminListReviews()); }
    catch (e) { toast.error('Failed to load reviews'); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const toggleVisibility = async (r) => {
    setBusy({ ...busy, [r.id]: true });
    try {
      await adminUpdateReview(r.id, { approved: !r.approved });
      toast.success(`Review ${r.approved ? 'hidden' : 'made visible'}`);
      reload();
    } catch (e) { toast.error('Update failed'); }
    finally { setBusy({ ...busy, [r.id]: false }); }
  };

  const remove = async (r) => {
    if (!window.confirm('Delete this review permanently?')) return;
    setBusy({ ...busy, [r.id]: true });
    try {
      await adminDeleteReview(r.id);
      toast.success('Review deleted');
      reload();
    } catch (e) { toast.error('Delete failed'); }
    finally { setBusy({ ...busy, [r.id]: false }); }
  };

  return (
    <div className="space-y-6" data-testid="reviews-section">
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-100 flex items-center gap-2"><Star size={18} /> Product & Service Reviews</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-zinc-500 text-sm p-4 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-zinc-500 text-sm p-4">No reviews yet. Users will be able to rate products & services after buying.</div>
          ) : (
            <div className="space-y-2">
              {rows.map((r) => (
                <div key={r.id} className={`rounded-lg border p-3 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start ${r.approved ? 'border-zinc-800 bg-zinc-950/60' : 'border-zinc-800 bg-zinc-900/40 opacity-70'}`} data-testid={`review-row-${r.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Stars n={r.rating} />
                      <span className="text-zinc-100 font-bold text-sm">{r.user_name}</span>
                      <span className="text-zinc-500 text-[11px]">on {r.item_type}/{r.item_id}</span>
                      {!r.approved && <span className="text-[10px] uppercase tracking-wider bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded">Hidden</span>}
                    </div>
                    {r.comment && <div className="text-zinc-300 text-sm mt-1">{r.comment}</div>}
                    <div className="text-zinc-500 text-[11px] mt-1">{fmt(r.created_at)}</div>
                  </div>
                  <div className="flex gap-1.5 flex-wrap md:justify-end">
                    <button onClick={() => toggleVisibility(r)} disabled={!!busy[r.id]} className="h-8 px-2.5 rounded-md text-[11px] font-bold border border-zinc-800 bg-zinc-900 text-zinc-200 hover:bg-zinc-800 disabled:opacity-60 flex items-center gap-1" data-testid={`review-toggle-${r.id}`}>
                      {r.approved ? <><EyeOff size={11} /> Hide</> : <><Eye size={11} /> Show</>}
                    </button>
                    <button onClick={() => remove(r)} disabled={!!busy[r.id]} className="h-8 px-2.5 rounded-md text-[11px] font-bold border border-red-900/40 bg-red-600/15 text-red-300 hover:bg-red-600/25 disabled:opacity-60 flex items-center gap-1" data-testid={`review-delete-${r.id}`}>
                      <Trash2 size={11} /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
